import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';

/**
 * PFC-13 — the legal entities' DPS signing keys and their signing sessions.
 *
 * The key password goes to the API in the body of an upload, a replace or an
 * unlock, and nowhere else: not in a store or the URL. TanStack Query keeps a
 * mutation's variables while the mutation is cached, so these mutations are
 * not cached (`gcTime: 0`) and the forms `reset()` them and clear the field
 * after each attempt.
 */

export type SigningKeyStatus = 'active' | 'expiring' | 'expired' | 'revoked' | 'replaced';

export type SigningCertificate = {
  ownerName: string;
  drfo: string | null;
  edrpou: string | null;
  serial: string;
  issuer: string;
  validFrom: string;
  validTo: string;
  keyUsage: string[];
};

export type SigningSession = {
  unlocked: boolean;
  expiresAt: string | null;
  unlockedBy: string | null;
  unlockedAt: string | null;
};

export type SigningKey = {
  id: string;
  legalEntity: { id: string; kind: 'fop' | 'llc'; tin: string; name: string };
  status: SigningKeyStatus;
  current: boolean;
  expiresAt: string;
  daysLeft: number;
  containerFileName: string | null;
  certificateFileNames: string[];
  signing: SigningCertificate;
  keyAgreement: SigningCertificate;
  createdAt: string;
  createdBy: string | null;
  replacedAt: string | null;
  replacedByKeyId: string | null;
  replacesKeyId: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  session: SigningSession;
};

export type SigningKeyList = { items: SigningKey[]; warnDays: number };

/**
 * PFC-20 — what an upload, a replace or an unlock may answer next to its
 * result when the server's OCSP mode is `warn`: the revocation status of a
 * certificate of the key could not be confirmed, and the key was accepted.
 */
export type SigningKeyWarnings = { warnings?: string[] };

export type KeyFile = { fileName: string; base64: string };
export type SigningKeyFiles = { container: KeyFile; certificates: KeyFile[]; password: string };

export const SIGNING_KEY_STATUS_LABEL_KEY: Record<SigningKeyStatus, string> = {
  active: 'fiscalCabinet.signingKeys.status.active',
  expiring: 'fiscalCabinet.signingKeys.status.expiring',
  expired: 'fiscalCabinet.signingKeys.status.expired',
  revoked: 'fiscalCabinet.signingKeys.status.revoked',
  replaced: 'fiscalCabinet.signingKeys.status.replaced',
};
export const SIGNING_KEY_STATUS_LABEL: Record<SigningKeyStatus, string> = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  revoked: 'Revoked',
  replaced: 'Replaced',
};

/**
 * What an operator reads for each code the API answers with (PFC-14's key
 * codes and the cabinet's own). Unknown codes fall back to a generic message.
 */
export const SIGNING_KEY_ERROR_KEY: Record<string, string> = {
  KEY_PASSWORD_INVALID: 'fiscalCabinet.signingKeys.error.KEY_PASSWORD_INVALID',
  KEY_UNLOCK_THROTTLED: 'fiscalCabinet.signingKeys.error.KEY_UNLOCK_THROTTLED',
  KEY_CONTAINER_UNREADABLE: 'fiscalCabinet.signingKeys.error.KEY_CONTAINER_UNREADABLE',
  KEY_CONTAINER_UNSUPPORTED: 'fiscalCabinet.signingKeys.error.KEY_CONTAINER_UNSUPPORTED',
  KEY_CERTIFICATE_UNREADABLE: 'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_UNREADABLE',
  KEY_NO_SIGNING_CERT: 'fiscalCabinet.signingKeys.error.KEY_NO_SIGNING_CERT',
  KEY_NO_ENCRYPTION_CERT: 'fiscalCabinet.signingKeys.error.KEY_NO_ENCRYPTION_CERT',
  KEY_TAX_ID_MISMATCH: 'fiscalCabinet.signingKeys.error.KEY_TAX_ID_MISMATCH',
  KEY_CERTIFICATE_EXPIRED: 'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_EXPIRED',
  KEY_CERTIFICATE_NOT_YET_VALID: 'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_NOT_YET_VALID',
  FISCAL_SIGNING_KEY_EXISTS: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_EXISTS',
  FISCAL_SIGNING_KEY_SAME: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_SAME',
  FISCAL_SIGNING_KEY_NOT_CURRENT: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_NOT_CURRENT',
  FISCAL_SIGNING_KEY_EXPIRED: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_EXPIRED',
  FISCAL_SIGNING_NOT_CONFIGURED: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_NOT_CONFIGURED',
  FISCAL_SIGNING_KEY_STORAGE_NOT_CONFIGURED:
    'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_STORAGE_NOT_CONFIGURED',
  FISCAL_LEGAL_ENTITY_INACTIVE: 'fiscalCabinet.signingKeys.error.FISCAL_LEGAL_ENTITY_INACTIVE',
  REQUEST_BODY_TOO_LARGE: 'fiscalCabinet.signingKeys.error.REQUEST_BODY_TOO_LARGE',
  REQUEST_BODY_UNREADABLE: 'fiscalCabinet.signingKeys.error.REQUEST_BODY_UNREADABLE',
  FISCAL_SIGNING_KEY_REVOKED: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_REVOKED',
  FISCAL_SIGNING_KEY_MISMATCH: 'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_MISMATCH',
  FISCAL_SIGNING_KEY_DECRYPT_FAILED:
    'fiscalCabinet.signingKeys.error.FISCAL_SIGNING_KEY_DECRYPT_FAILED',
  FISCAL_LEGAL_ENTITY_NOT_FOUND: 'fiscalCabinet.signingKeys.error.FISCAL_LEGAL_ENTITY_NOT_FOUND',
  // PFC-20: every signer code the API can answer.
  KEY_SESSION_LOCKED: 'fiscalCabinet.signingKeys.error.KEY_SESSION_LOCKED',
  KEY_CERTIFICATE_OUTSIDE_VALIDITY:
    'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_OUTSIDE_VALIDITY',
  KEY_REVOKED_OR_REPLACED: 'fiscalCabinet.signingKeys.error.KEY_REVOKED_OR_REPLACED',
  KEY_CERTIFICATE_REVOKED: 'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_REVOKED',
  KEY_CERTIFICATE_STATUS_UNKNOWN: 'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_STATUS_UNKNOWN',
  KEY_CERTIFICATE_STATUS_UNAVAILABLE:
    'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_STATUS_UNAVAILABLE',
  KEY_CERTIFICATE_STATUS_UNSUPPORTED:
    'fiscalCabinet.signingKeys.error.KEY_CERTIFICATE_STATUS_UNSUPPORTED',
  KEY_NO_TAX_ID: 'fiscalCabinet.signingKeys.error.KEY_NO_TAX_ID',
  SEAL_HEADER_INVALID: 'fiscalCabinet.signingKeys.error.SEAL_HEADER_INVALID',
  REPLY_MALFORMED: 'fiscalCabinet.signingKeys.error.REPLY_MALFORMED',
  REPLY_NOT_FOR_THIS_KEY: 'fiscalCabinet.signingKeys.error.REPLY_NOT_FOR_THIS_KEY',
  REPLY_ORIGINATOR_UNKNOWN: 'fiscalCabinet.signingKeys.error.REPLY_ORIGINATOR_UNKNOWN',
  REPLY_NOT_SIGNED: 'fiscalCabinet.signingKeys.error.REPLY_NOT_SIGNED',
  REPLY_DIGEST_UNSUPPORTED: 'fiscalCabinet.signingKeys.error.REPLY_DIGEST_UNSUPPORTED',
  REPLY_SIGNATURE_INVALID: 'fiscalCabinet.signingKeys.error.REPLY_SIGNATURE_INVALID',
  REPLY_SIGNER_UNTRUSTED: 'fiscalCabinet.signingKeys.error.REPLY_SIGNER_UNTRUSTED',
  REPLY_TIMESTAMP_INVALID: 'fiscalCabinet.signingKeys.error.REPLY_TIMESTAMP_INVALID',
  REPLY_SIGNER_REVOKED: 'fiscalCabinet.signingKeys.error.REPLY_SIGNER_REVOKED',
  REPLY_SIGNER_STATUS_UNVERIFIED: 'fiscalCabinet.signingKeys.error.REPLY_SIGNER_STATUS_UNVERIFIED',
  SIGNER_WORKER_FAILED: 'fiscalCabinet.signingKeys.error.SIGNER_WORKER_FAILED',
  SIGNER_TIMEOUT: 'fiscalCabinet.signingKeys.error.SIGNER_TIMEOUT',
  SIGNER_INTERNAL_ERROR: 'fiscalCabinet.signingKeys.error.SIGNER_INTERNAL_ERROR',
};
export const SIGNING_KEY_ERROR: Record<string, string> = {
  KEY_PASSWORD_INVALID: 'The key password is incorrect.',
  KEY_UNLOCK_THROTTLED: 'Too many wrong passwords. Try again in {seconds} s.',
  KEY_CONTAINER_UNREADABLE: 'This file is not a key the cabinet can read.',
  KEY_CONTAINER_UNSUPPORTED:
    'This key format is not supported yet. Java key stores (JKS, e.g. PrivatBank keys) cannot be used; upload a Key-6.dat or a PKCS#12 (.pfx, .p12) key.',
  KEY_CERTIFICATE_UNREADABLE: 'A certificate file could not be read.',
  KEY_NO_SIGNING_CERT:
    'The key has no signing certificate. Add the certificate files that came with the key.',
  KEY_NO_ENCRYPTION_CERT:
    'The key has no encryption certificate. Add the certificate files that came with the key.',
  KEY_TAX_ID_MISMATCH: 'The key belongs to another taxpayer, not to this legal entity.',
  KEY_CERTIFICATE_EXPIRED: 'A certificate of the key has expired.',
  KEY_CERTIFICATE_NOT_YET_VALID: 'A certificate of the key is not valid yet.',
  FISCAL_SIGNING_KEY_EXISTS: 'This legal entity already has a key. Replace it instead.',
  FISCAL_SIGNING_KEY_SAME: 'This is the key being replaced. Choose the new key.',
  FISCAL_SIGNING_KEY_NOT_CURRENT: 'The key has been revoked or replaced.',
  FISCAL_SIGNING_KEY_EXPIRED: 'The key has expired. Upload a new one.',
  FISCAL_SIGNING_NOT_CONFIGURED:
    'Signing is not set up on this server yet. Ask your administrator.',
  FISCAL_SIGNING_KEY_STORAGE_NOT_CONFIGURED:
    'Key storage is not set up on this server yet. Ask your administrator.',
  FISCAL_LEGAL_ENTITY_INACTIVE: 'The legal entity is retired.',
  REQUEST_BODY_TOO_LARGE: 'The files are too large for a key.',
  REQUEST_BODY_UNREADABLE: 'The request could not be read. Try again.',
  FISCAL_SIGNING_KEY_REVOKED: 'This key was revoked and cannot be used again.',
  FISCAL_SIGNING_KEY_MISMATCH: 'The stored key does not match its record. Upload the key again.',
  FISCAL_SIGNING_KEY_DECRYPT_FAILED:
    'The stored key could not be decrypted on this server. Ask your administrator.',
  FISCAL_LEGAL_ENTITY_NOT_FOUND: 'The legal entity no longer exists.',
  KEY_SESSION_LOCKED: 'The key is locked. Unlock it and try again.',
  KEY_CERTIFICATE_OUTSIDE_VALIDITY:
    'A certificate of the key is not valid at this moment, so nothing was signed. Upload a new key.',
  KEY_REVOKED_OR_REPLACED:
    'The key was revoked or replaced after it was unlocked, so nothing was signed.',
  KEY_CERTIFICATE_REVOKED:
    'The certification authority has revoked a certificate of this key. It cannot be used.',
  KEY_CERTIFICATE_STATUS_UNKNOWN:
    'The certification authority does not know a certificate of this key, so it cannot be used.',
  KEY_CERTIFICATE_STATUS_UNAVAILABLE:
    'The certificate status of this key could not be checked with the certification authority. Try again later.',
  KEY_CERTIFICATE_STATUS_UNSUPPORTED:
    'Only keys issued by the tax service certification authority (КНЕДП ДПС) can be checked for revocation, so this key cannot be used here.',
  KEY_NO_TAX_ID: 'Neither the document nor the key names the taxpayer.',
  SEAL_HEADER_INVALID:
    'The document name or taxpayer number contains characters the tax service does not accept.',
  REPLY_MALFORMED: 'The reply from the tax service could not be read.',
  REPLY_NOT_FOR_THIS_KEY: 'The reply from the tax service is addressed to a different key.',
  REPLY_ORIGINATOR_UNKNOWN:
    'The reply comes from a tax service certificate the cabinet does not know.',
  REPLY_NOT_SIGNED: 'The reply from the tax service is not signed, so it was not accepted.',
  REPLY_DIGEST_UNSUPPORTED:
    'The reply is signed in a way the cabinet does not support, so it was not accepted.',
  REPLY_SIGNATURE_INVALID:
    'The signature on the reply does not match its content, so it was not accepted.',
  REPLY_SIGNER_UNTRUSTED: 'The reply is not signed by the tax service, so it was not accepted.',
  REPLY_TIMESTAMP_INVALID: 'The time stamp on the reply does not verify, so it was not accepted.',
  REPLY_SIGNER_REVOKED:
    'The certificate that signed the reply had been revoked, so it was not accepted.',
  REPLY_SIGNER_STATUS_UNVERIFIED:
    'The status of the certificate that signed the reply could not be checked. Try again later.',
  SIGNER_WORKER_FAILED:
    'Signing stopped unexpectedly. Try again; a key that was unlocked must be unlocked again.',
  SIGNER_TIMEOUT: 'Signing took too long and was stopped. Unlock the key again and try again.',
  SIGNER_INTERNAL_ERROR: 'Signing failed. Try again.',
};

/** PFC-20 — the warnings an upload, replace or unlock may carry (OCSP in `warn` mode). */
export const SIGNING_KEY_WARNING_KEY: Record<string, string> = {
  KEY_CERTIFICATE_STATUS_UNKNOWN:
    'fiscalCabinet.signingKeys.warning.KEY_CERTIFICATE_STATUS_UNKNOWN',
  KEY_CERTIFICATE_STATUS_UNAVAILABLE:
    'fiscalCabinet.signingKeys.warning.KEY_CERTIFICATE_STATUS_UNAVAILABLE',
  KEY_CERTIFICATE_STATUS_UNSUPPORTED:
    'fiscalCabinet.signingKeys.warning.KEY_CERTIFICATE_STATUS_UNSUPPORTED',
};
export const SIGNING_KEY_WARNING: Record<string, string> = {
  KEY_CERTIFICATE_STATUS_UNKNOWN:
    'The certification authority does not know a certificate of this key. The key was accepted, but check it before signing.',
  KEY_CERTIFICATE_STATUS_UNAVAILABLE:
    'The certificate status of this key could not be checked with the certification authority. The key was accepted anyway.',
  KEY_CERTIFICATE_STATUS_UNSUPPORTED:
    'Only keys issued by the tax service certification authority (КНЕДП ДПС) can be checked for revocation. The key was accepted without that check.',
};

/** The operator's text for each warning code it knows; unknown codes are left out. */
export function signingKeyWarningMessages(
  warnings: readonly string[] | undefined,
  t: (key: string, fallback: string) => string,
): string[] {
  return (warnings ?? []).flatMap((code) => {
    const key = SIGNING_KEY_WARNING_KEY[code];
    const fallback = SIGNING_KEY_WARNING[code];
    return key === undefined || fallback === undefined ? [] : [t(key, fallback)];
  });
}

/** The API's refusal: its stable code and, when throttled, how long to wait. */
export function signingKeyFailure(err: unknown): {
  code: string | null;
  retryAfterSeconds: number | null;
} {
  if (isAxiosError(err)) {
    const body = err.response?.data as { code?: unknown; retryAfterSeconds?: unknown } | undefined;
    return {
      code: typeof body?.code === 'string' ? body.code : null,
      retryAfterSeconds:
        typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null,
    };
  }
  return { code: null, retryAfterSeconds: null };
}

const ROOT = ['fiscal-cabinet', 'signing-keys'] as const;

export function useFiscalSigningKeys(scope: 'current' | 'all', enabled: boolean) {
  return useQuery({
    queryKey: [...ROOT, scope] as const,
    enabled,
    queryFn: async (): Promise<SigningKeyList> =>
      (await apiClient.get<SigningKeyList>('/fiscal-cabinet/signing-keys', { params: { scope } }))
        .data,
  });
}

function useKeyMutation<V, R>(run: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    gcTime: 0,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useUploadSigningKey() {
  return useKeyMutation(
    async (body: SigningKeyFiles & { legalEntityId: string }) =>
      (await apiClient.post<SigningKey & SigningKeyWarnings>('/fiscal-cabinet/signing-keys', body))
        .data,
  );
}

export function useReplaceSigningKey() {
  return useKeyMutation(
    async ({ id, body }: { id: string; body: SigningKeyFiles }) =>
      (
        await apiClient.post<SigningKey & SigningKeyWarnings>(
          `/fiscal-cabinet/signing-keys/${id}/replace`,
          body,
        )
      ).data,
  );
}

export function useRevokeSigningKey() {
  return useKeyMutation(
    async ({ id, reason }: { id: string; reason: string }) =>
      (await apiClient.post<SigningKey>(`/fiscal-cabinet/signing-keys/${id}/revoke`, { reason }))
        .data,
  );
}

export function useUnlockSigningKey() {
  return useKeyMutation(
    async ({ id, password }: { id: string; password: string }) =>
      (
        await apiClient.post<SigningSession & SigningKeyWarnings>(
          `/fiscal-cabinet/signing-keys/${id}/unlock`,
          { password },
        )
      ).data,
  );
}

export function useLockSigningKey() {
  return useKeyMutation(
    async (id: string) =>
      (await apiClient.post<SigningSession>(`/fiscal-cabinet/signing-keys/${id}/lock`, {})).data,
  );
}

/** A picked file as the API takes it (base64 of its bytes). */
export function readKeyFile(file: File): Promise<KeyFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The file could not be read'));
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      resolve({ fileName: file.name, base64: url.slice(url.indexOf(',') + 1) });
    };
    reader.readAsDataURL(file);
  });
}
